import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { ProductSelectedOption } from '@models/commerce';
import { DerivedScheduleData, ProductEditModel, QuestionOptionEditModel } from '@models/product';
import {
  CalendarCartLine,
  convertCalendarCartToCheckoutPayload,
  isCalendarOptionProduct,
} from '@utils/product-option-date';
import { getModalQuestionOptions } from '@utils/preferred-schedule';
import AppServiceCautionsModal from './AppServiceCautionsModal';
import './ProductDetailView.scss';
import { ClassInquiryModal } from './product-detail/ClassInquiryModal';
import { CommerceOptionModal } from './product-detail/CommerceOptionModal';
import { CommercePaymentModal } from './product-detail/CommercePaymentModal';
import { ProductCalendarCartSheet } from './product-detail/ProductCalendarCartSheet';
import { ProductDetailContentsView } from './product-detail/ProductDetailContentsView';
import { RequestReopenAlarmModal } from './product-detail/RequestReopenAlarmModal';

const HEADER_HEIGHT = '56px';

export const ProductDetailView: React.FC<{ product: ProductEditModel; openOptionModal?: boolean }> = (props) => {
  const { pathname } = useLocation();
  const [headerOnImage, setHeaderOnImage] = useState<boolean>(true);
  const [scrollFocusOn, setScrollFocusOn] = useState<string>('detail');
  const [showCautions, setShowCautions] = useState<boolean>(false);
  const productDetailInfoRef = useRef<any>();
  const productEtcInfoRef = useRef<any>();
  const scrollTopRef = useRef<any>();
  const [showRequestReopenAlarm, setShowRequestReopenAlarm] = useState<boolean>(false);
  const [showRequestChat, setShowRequestChat] = useState<boolean>(false);
  const [showOptionModal, setShowOptionModal] = useState<boolean>(props.openOptionModal ?? false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [totalFee, setTotalFee] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<ProductSelectedOption>({ options: [] });
  const [qnaOptions, setQnaOptions] = useState<QuestionOptionEditModel[]>([]);
  const [preferredScheduleAnswer, setPreferredScheduleAnswer] = useState<string>('');
  const [calendarCartLines, setCalendarCartLines] = useState<CalendarCartLine[]>([]);
  const [showCalendarCartSheet, setShowCalendarCartSheet] = useState<boolean>(false);
  const [derivedScheduleData, setDerivedScheduleData] = useState<DerivedScheduleData>({
    schedules: [],
    additionalPurchaseOptions: [],
  });

  const data = props.product;

  const isCalendarProduct = useMemo(
    () => isCalendarOptionProduct(data?.productOptions, { forceOptionModal: data?.forceOptionModal }),
    [data?.productOptions, data?.forceOptionModal],
  );

  const calendarCartCount = calendarCartLines.reduce((sum, line) => sum + line.optionCount, 0);
  const calendarCartTotalFee = calendarCartLines.reduce((sum, line) => sum + line.lineTotal, 0);

  useEffect(() => {
    if (scrollTopRef.current) {
      scrollTopRef.current.scrollIntoView({ block: 'start' });
    }
  }, [pathname]);

  useEffect(() => {
    document.title = data?.title ? `아이고고 | ${data.title}` : '아이고고';
    return () => {
      document.title = '아이고고 | 파트너센터';
    };
  }, [data?.title]);

  useEffect(() => {
    setCalendarCartLines([]);
    setShowCalendarCartSheet(false);
    setPreferredScheduleAnswer('');
    setQnaOptions([]);
  }, [data?._id, isCalendarProduct]);

  useEffect(() => {
    if (isCalendarProduct) {
      setShowOptionModal(false);
    } else if (props.openOptionModal) {
      setShowOptionModal(true);
    }
  }, [isCalendarProduct, props.openOptionModal]);

  const openOptionModal = () => {
    setShowOptionModal(true);
  };

  const openCalendarCheckout = () => {
    if (calendarCartLines.length === 0) {
      toast.error('옵션을 담아주세요.');
      return;
    }

    const payload = convertCalendarCartToCheckoutPayload(calendarCartLines);
    setSelectedOptions(payload.selectedOptions);
    setDerivedScheduleData(payload.scheduleInfo);
    setTotalFee(payload.totalFee);
    setQnaOptions(
      getModalQuestionOptions(data?.questionOptions).map((item) => ({
        question: item.question,
        questionType: item.questionType,
        preferredSchedule: item.preferredSchedule,
        answer: '',
      })),
    );
    setShowCalendarCartSheet(false);
    setShowPaymentModal(true);
  };

  return (
    <div style={{ position: 'relative', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <AppServiceCautionsModal
        open={showCautions}
        onCancel={() => {
          setShowCautions(false);
        }}></AppServiceCautionsModal>
      <RequestReopenAlarmModal
        open={showRequestReopenAlarm}
        productId={data?._id}
        onCancel={() => {
          setShowRequestReopenAlarm(false);
        }}></RequestReopenAlarmModal>
      <ClassInquiryModal
        open={showRequestChat}
        onCancel={() => {
          setShowRequestChat(false);
        }}
        onConfirm={() => {
          setShowRequestChat(false);
        }}></ClassInquiryModal>
      <CommerceOptionModal
        open={showOptionModal && !isCalendarProduct}
        product={data}
        preferredScheduleAnswer={preferredScheduleAnswer}
        onChangePreferredScheduleAnswer={setPreferredScheduleAnswer}
        onChangeTotalFee={(value) => {
          setTotalFee(value);
        }}
        onChangeSelectedOptions={(options) => {
          setSelectedOptions(options);
        }}
        onChangeQnaOptions={(options) => {
          setQnaOptions(options);
        }}
        onChangeScheduleInfo={(value) => {
          setDerivedScheduleData(value);
        }}
        onCancel={() => {
          setShowOptionModal(false);
        }}
        onConfirm={() => {
          setShowOptionModal(false);
          setShowPaymentModal(true);
        }}></CommerceOptionModal>
      <ProductCalendarCartSheet
        open={showCalendarCartSheet}
        lines={calendarCartLines}
        onClose={() => setShowCalendarCartSheet(false)}
        onRemove={(id) => {
          setCalendarCartLines((prev) => {
            const next = prev.filter((line) => line.id !== id);
            if (next.length === 0) {
              setShowCalendarCartSheet(false);
            }
            return next;
          });
        }}
        onPurchase={openCalendarCheckout}
      />
      <CommercePaymentModal
        open={showPaymentModal}
        qnaOptions={qnaOptions}
        scheduleInfo={derivedScheduleData}
        selectedOptions={selectedOptions}
        collectQnaInPayment={isCalendarProduct}
        onChangeQnaOptions={setQnaOptions}
        onClickBack={() => {
          setShowPaymentModal(false);
          if (isCalendarProduct) {
            setShowCalendarCartSheet(calendarCartLines.length > 0);
          } else {
            setShowOptionModal(true);
          }
        }}
        onConfirm={() => {
          setShowPaymentModal(false);
          if (isCalendarProduct) {
            setCalendarCartLines([]);
          }
        }}
        product={data}
        totalFee={totalFee}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          height: HEADER_HEIGHT,
          backgroundColor: headerOnImage ? 'transparent' : '#ffffff',
          borderBottom: headerOnImage ? 'none' : '1px solid #eff1f4',
        }}></div>
      <div style={{ marginTop: '-56px', height: '100%', overflow: 'hidden' }}>
        <ProductDetailContentsView
          data={data}
          isCalendarOptionProduct={isCalendarProduct}
          calendarCartCount={calendarCartCount}
          calendarCartTotalFee={calendarCartTotalFee}
          onAddCalendarCartLine={(line) => {
            setCalendarCartLines((prev) => [...prev, line]);
            setShowCalendarCartSheet(false);
          }}
          onOpenCalendarCartSheet={() => {
            if (calendarCartLines.length > 0) {
              setShowCalendarCartSheet(true);
            }
          }}
          onPurchaseCalendarCart={openCalendarCheckout}
          setHeaderOnImage={setHeaderOnImage}
          scrollFocusOn={scrollFocusOn}
          setScrollFocusOn={setScrollFocusOn}
          setShowRequestChat={setShowRequestChat}
          setShowRequestReopenAlarm={setShowRequestReopenAlarm}
          setShowCautions={setShowCautions}
          setShowOptionModal={openOptionModal}
          scrollTopRef={scrollTopRef}
          productDetailInfoRef={productDetailInfoRef}
          productEtcInfoRef={productEtcInfoRef}
        />
      </div>
    </div>
  );
};

export default ProductDetailView;
